// src\components\account\widgets\widgetItem\StickyStoryWidget.tsx
"use client"

import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/Heading";
import { useDictionary } from "@/core/hooks/use-dictionary";
import { Plus, Trash2 } from "lucide-react";
import { Control, Controller, FieldErrors, useFieldArray, UseFormRegister } from "react-hook-form";
import TextInput from "../../input/TextInput";
import TextareaInput from "../../input/TextareaInput";
import FileUploadInput from "../../input/MultipleFileUploadInput";

interface StickyStoryWidgetProps {
    widgetIndex: number;
    name: string;
    control: Control<any>;
    register: UseFormRegister<any>;
    errors: FieldErrors<any>;
    disabled?: boolean;
}

export const StickyStoryWidget = ({
    widgetIndex,
    name,
    control,
    register,
    errors,
    disabled,
}: StickyStoryWidgetProps) => {
    const { dictionary } = useDictionary();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.${widgetIndex}.items`,
    });

    return (
        <div className="col-span-2 space-y-4">
            <TextInput
                name={`${name}.${widgetIndex}.title`}
                label={dictionary.forms?.title}
                placeholder="عنوان بخش (اختیاری)"
                register={register}
                errors={errors}
                disabled={disabled}
            />

            <Controller
                control={control}
                name={`${name}.${widgetIndex}.image`}
                render={({ field }) => (
                    <FileUploadInput
                        accept="image/*"
                        disabled={disabled}
                        onUploadSuccess={(image) => {
                            if (!Array.isArray(image) && image) field.onChange(image);
                        }}
                        defaultImages={
                            field.value
                                ? [{ ...field.value, id: field.value.id ?? 0 }]
                                : []
                        }
                    />
                )}
            />

            {fields.map((field, itemIndex) => (
                <div key={field.id} className="border p-4 rounded-md space-y-3">
                    <div className="flex justify-between items-center">
                        <Heading level={4}>مرحله {itemIndex + 1}</Heading>
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => fields.length > 1 && remove(itemIndex)}
                            disabled={fields.length <= 1 || disabled}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>

                    <TextInput
                        name={`${name}.${widgetIndex}.items.${itemIndex}.title`}
                        label={dictionary.forms?.title}
                        placeholder="عنوان مرحله"
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                    <TextInput
                        name={`${name}.${widgetIndex}.items.${itemIndex}.subtitle`}
                        label="زیرعنوان (اختیاری)"
                        placeholder=""
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                    <TextareaInput
                        name={`${name}.${widgetIndex}.items.${itemIndex}.description`}
                        label={dictionary.forms?.description}
                        register={register}
                        errors={errors}
                        disabled={disabled}
                    />
                </div>
            ))}

            <Button
                type="button"
                variant="outline"
                onClick={() => append({ title: "", subtitle: "", description: "" })}
                disabled={disabled}
            >
                <Plus className="h-4 w-4 mr-2" />
                {dictionary.common.add + " مرحله"}
            </Button>
        </div>
    );
};